import { useState, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOffIcon, Loader2Icon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import type { LoginDTO } from '@shared/schemas/auth';
import { loginSchema } from '@shared/schemas/auth';
import { authClient } from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { AnimatedBrandLogo } from '@/components/animated-brand-logo';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const inputAuthClass =
  'h-12 rounded-lg border border-zinc-200 bg-white px-4 text-[15px] text-zinc-900 shadow-xs transition-colors placeholder:text-zinc-400 hover:border-zinc-300 focus-visible:border-zinc-950 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-950 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:hover:border-zinc-700 dark:focus-visible:border-zinc-100 dark:focus-visible:ring-zinc-100';

const labelAuthClass = 'text-sm font-medium text-zinc-800 dark:text-zinc-200';

type LoginFormProps = React.ComponentProps<'form'> & {
  redirectTo?: string;
};

export function LoginForm({
  className,
  redirectTo,
  ...props
}: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const redirectTarget = useMemo(() => {
    if (!redirectTo || !redirectTo.startsWith('/') || redirectTo.startsWith('//')) {
      return '/';
    }
    return redirectTo;
  }, [redirectTo]);

  const form = useForm<LoginDTO>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  async function handleLogin(values: LoginDTO) {
    form.clearErrors('root');

    const result = await authClient.signIn.email({
      email: values.email,
      password: values.password,
      rememberMe: true,
    });

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);

      if ('code' in result.error && result.error.code === 'EMAIL_NOT_VERIFIED') {
        navigate(`/auth/verify-email?email=${encodeURIComponent(values.email)}`);
      }

      return;
    }

    await authClient.getSession();
    toast.success('Connexion réussie.');
    navigate(redirectTarget, { replace: true });
  }

  return (
    <form
      className={cn('flex flex-col gap-0', className)}
      onSubmit={form.handleSubmit(handleLogin)}
      {...props}
    >
      <FieldGroup className="gap-5">
        {/* Header Branding & Title */}
        <div className="auth-reveal-stagger-1 flex flex-col items-center pb-2 text-center">
          <Link
            to="/"
            className="mb-6 inline-flex items-center justify-center transition-opacity hover:opacity-85 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/10 rounded-lg"
            aria-label="Inersia - Accueil"
          >
            <AnimatedBrandLogo variant="hero" appearance="auto" className="h-10 w-auto sm:h-11 md:h-12" />
          </Link>
          <h1 className="text-balance text-2xl font-bold tracking-tight text-zinc-950 sm:text-[1.75rem] dark:text-zinc-50">
            Connexion à votre compte
          </h1>
        </div>

        <Field className="auth-reveal-stagger-2 gap-2">
          <FieldLabel htmlFor="email" className={labelAuthClass}>
            Email
          </FieldLabel>
          <Input
            id="email"
            data-testid="login-email"
            type="email"
            autoComplete="email"
            placeholder="anne.onyme@example.com"
            className={inputAuthClass}
            aria-invalid={form.formState.errors.email ? 'true' : 'false'}
            {...form.register('email')}
          />
          <FieldError errors={[form.formState.errors.email]} />
        </Field>

        <Field className="auth-reveal-stagger-3 gap-2">
          <div className="flex items-center justify-between gap-3">
            <FieldLabel htmlFor="password" className={labelAuthClass}>
              Mot de passe
            </FieldLabel>
            <Link
              className="shrink-0 text-xs font-normal text-zinc-500 transition-colors hover:text-zinc-900 hover:underline dark:text-zinc-400 dark:hover:text-zinc-100"
              to="/auth/forgot-password"
            >
              Mot de passe oublié ?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              data-testid="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={cn(inputAuthClass, 'pr-11')}
              aria-invalid={form.formState.errors.password ? 'true' : 'false'}
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 top-0 flex h-full items-center px-3.5 text-zinc-400 hover:text-zinc-700 transition-colors focus:outline-none dark:text-zinc-500 dark:hover:text-zinc-300"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              tabIndex={-1}
            >
              {showPassword ? <EyeOffIcon className="size-4.5" /> : <EyeIcon className="size-4.5" />}
            </button>
          </div>
          <FieldError errors={[form.formState.errors.password]} />
        </Field>

        <FieldError errors={[form.formState.errors.root]} />

        <Field className="auth-reveal-stagger-4 pt-2">
          <Button
            data-testid="login-submit"
            type="submit"
            size="lg"
            className="h-12 w-full rounded-lg bg-zinc-950 text-[15px] font-semibold text-white shadow-xs transition-all duration-150 hover:bg-zinc-800 active:scale-[0.99] dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-100"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? <Loader2Icon className="size-4.5 animate-spin" /> : null}
            Se connecter
          </Button>
        </Field>

        <p className="auth-reveal-stagger-5 pt-3 text-center text-sm text-zinc-500 dark:text-zinc-400">
          Pas encore de compte ?{' '}
          <a
            href="https://inersio.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-zinc-900 underline underline-offset-4 hover:text-zinc-700 transition-colors dark:text-zinc-100"
          >
            Cliquez ici
          </a>
        </p>
      </FieldGroup>
    </form>
  );
}
